import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";

/**
 * Shared client-side filters for school-program report views
 * (School Program Attendance, Registered Student List - School Program).
 *
 * Holds the filter state, loads the dropdown options (same sources as the
 * School Programs page) and returns the rows that pass the active filters.
 *
 * Unlike camps, school programs are tied to a school and run on a single
 * weekday, so the filters are School / Program Type / Day / Stream.
 *
 * `programField` is the row field the Program Type filter compares against:
 * the attendance endpoint returns it as `program_name`, the registered-list
 * endpoint as `program_type`.
 */
export function useSchoolProgramFilters(
  reports,
  branch,
  { programField = "program_type" } = {}
) {
  const [schoolFilter, setSchoolFilter] = useState(""); // school_information_id
  const [dayFilter, setDayFilter] = useState("");
  const [programTypeFilter, setProgramTypeFilter] = useState("");
  const [streamFilter, setStreamFilter] = useState([]); // array of course_code
  const [schoolOptions, setSchoolOptions] = useState([]); // for react-select
  const [programTypes, setProgramTypes] = useState([]);
  const [streamOptions, setStreamOptions] = useState([]); // grouped for react-select

  // Day options come from the distinct values present in the results.
  const dayOptions = useMemo(
    () =>
      [...new Set((reports || []).map((r) => r.day).filter(Boolean))].sort(),
    [reports]
  );

  // School + Program Type + Stream Name options mirror the School Programs page.
  useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      try {
        const [schoolRes, progRes, courseRes] = await Promise.all([
          axios(`api/school-programs/schools/${branch.branch_id}`),
          axios("api/school-programs/program-type"),
          axios(`api/school-programs/course/${branch.branch_id}`),
        ]);
        if (cancelled) return;

        setSchoolOptions(
          (schoolRes.data.results || []).map((sc) => ({
            value: sc.id,
            label: sc.value,
          }))
        );

        const programs = progRes.data.results || [];
        const courses = courseRes.data.results || [];
        setProgramTypes(programs);

        const grouped = programs.map((pt) => ({
          label: pt.value,
          options: courses
            .filter((c) => String(c.program_type) === String(pt.id))
            .sort((a, b) => (a.value > b.value ? 1 : -1))
            .map((a) => ({ value: a.value, label: `${a.value} - ${a.abbr}` })),
        }));
        setStreamOptions(grouped);
      } catch (e) {
        console.log("School program filter options error: ", e);
      }
    };
    if (branch && branch.branch_id) loadOptions();
    return () => {
      cancelled = true;
    };
  }, [branch]);

  const filteredReports = useMemo(() => {
    return (reports || []).filter((r) => {
      if (
        schoolFilter &&
        String(r.school_information_id) !== String(schoolFilter)
      )
        return false;
      if (dayFilter && r.day !== dayFilter) return false;
      if (programTypeFilter && r[programField] !== programTypeFilter)
        return false;
      if (streamFilter.length > 0 && !streamFilter.includes(r.course_code))
        return false;
      return true;
    });
  }, [
    reports,
    schoolFilter,
    dayFilter,
    programTypeFilter,
    streamFilter,
    programField,
  ]);

  return {
    filteredReports,
    filters: {
      schoolFilter,
      setSchoolFilter,
      dayFilter,
      setDayFilter,
      programTypeFilter,
      setProgramTypeFilter,
      setStreamFilter,
      schoolOptions,
      dayOptions,
      programTypes,
      streamOptions,
    },
  };
}

export default function SchoolProgramFilters({ filters }) {
  const {
    schoolFilter,
    setSchoolFilter,
    dayFilter,
    setDayFilter,
    programTypeFilter,
    setProgramTypeFilter,
    setStreamFilter,
    schoolOptions,
    dayOptions,
    programTypes,
    streamOptions,
  } = filters;

  return (
    <div className="form-group row align-items-end">
      <div className="col-lg-3">
        <Select
          isClearable
          placeholder="Select School"
          options={schoolOptions}
          value={schoolOptions.find((o) => o.value === schoolFilter) || null}
          onChange={(selected) =>
            setSchoolFilter(selected ? selected.value : "")
          }
        />
        <small className="form-text text-muted">
          <b>Filter</b> by School
        </small>
      </div>

      <div className="col-lg-3">
        <select
          className="form-control"
          value={dayFilter}
          onChange={(e) => setDayFilter(e.target.value)}
        >
          <option value="">All</option>
          {dayOptions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <small className="form-text text-muted">
          <b>Filter</b> by Day
        </small>
      </div>

      <div className="col-lg-3">
        <select
          className="form-control"
          value={programTypeFilter}
          onChange={(e) => setProgramTypeFilter(e.target.value)}
        >
          <option value="">All</option>
          {programTypes.map((pt) => (
            <option key={pt.id} value={pt.value}>
              {pt.value}
            </option>
          ))}
        </select>
        <small className="form-text text-muted">
          <b>Filter</b> by Program Type
        </small>
      </div>

      <div className="col-lg-3">
        <Select
          isMulti
          placeholder="Select..."
          options={streamOptions}
          onChange={(selected) =>
            setStreamFilter((selected || []).map((c) => c.value))
          }
        />
        <small className="form-text text-muted">
          <b>Filter</b> by Stream Name
        </small>
      </div>
    </div>
  );
}
